import { resolveTableV2BodyRowHeight } from '@/features/shared/components/tableSystem/TableV2Heights';
import { tableV2RowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { s } from '@/theme/tokens';
import { Box, TableCell, TableRow, Text } from '@/ui/primitives';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

const tableV2StateTextFontSize = '15px';
const tableV2StateVisibleRows = 5;

type RenderTableV2StateRowsArgs = {
  ariaLabel: string;
  columnCount: number;
  stateText: string;
};

type RenderTableV2FillerRowsArgs = {
  ariaLabel: string;
  columnCount: number;
  fillerRowCount: number;
};

const tableV2StateMessageRowIndex = Math.floor(tableV2StateVisibleRows / 2);

const buildTableV2StateRowSx = (rowHeight: string): SxProps<Theme> => ({
  height: rowHeight,
  maxHeight: rowHeight,
});

const buildTableV2StateMessageCellSx = (rowHeight: string): SxProps<Theme> => ({
  borderBottom: '0 !important',
  height: rowHeight,
  maxHeight: 'none',
  p: s('none'),
});

const tableV2StateMessageBoxSx = {
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
} satisfies SxProps<Theme>;

const tableV2StateMessageTextSx = {
  '&&': {
    fontSize: tableV2StateTextFontSize,
    fontWeight: (theme: Theme) => theme.typography.fontWeightRegular,
    lineHeight: 1.15,
  },
} satisfies SxProps<Theme>;

const buildTableV2StateSpacerCellSx = (rowHeight: string): SxProps<Theme> => ({
  borderBottom: '0 !important',
  fontSize: 0,
  height: rowHeight,
  lineHeight: 0,
  p: s('none'),
});

const buildTableV2FillerRowSx = (rowHeight: string): SxProps<Theme> => [
  tableV2RowSurfaceSx,
  {
    height: rowHeight,
    maxHeight: rowHeight,
    '&&': {
      height: rowHeight,
      maxHeight: rowHeight,
    },
    '&& > td': {
      boxSizing: 'border-box',
      height: rowHeight,
      maxHeight: rowHeight,
      py: s('none'),
      verticalAlign: 'middle',
    },
  },
];

const buildTableV2FillerCellSx = ({
  isLastFillerRow,
  rowHeight,
}: {
  isLastFillerRow: boolean;
  rowHeight: string;
}): SxProps<Theme> => ({
  borderBottom: isLastFillerRow
    ? '0 !important'
    : (theme: Theme) => `1px solid ${theme.app.colors.border}`,
  boxSizing: 'border-box',
  fontSize: 0,
  height: rowHeight,
  lineHeight: 0,
  maxHeight: rowHeight,
  minHeight: rowHeight,
  p: s('none'),
});

const tableV2FillerBoxSx = {
  display: 'block',
  height: '100%',
  minHeight: 0,
  width: '100%',
} satisfies SxProps<Theme>;

export const renderTableV2StateRows = ({
  ariaLabel,
  columnCount,
  stateText,
}: RenderTableV2StateRowsArgs): ReactElement[] => {
  const rowHeight = resolveTableV2BodyRowHeight(false);

  return Array.from({ length: tableV2StateVisibleRows }, (_item, index) => {
    const isMessageRow = index === tableV2StateMessageRowIndex;

    return (
      <TableRow key={`${ariaLabel}-empty-${String(index)}`} sx={buildTableV2StateRowSx(rowHeight)}>
        {isMessageRow ? (
          <TableCell colSpan={columnCount} sx={buildTableV2StateMessageCellSx(rowHeight)}>
            <Box aria-atomic="true" aria-live="polite" role="status" sx={tableV2StateMessageBoxSx}>
              <Text role="body" color="textSecondary" sx={tableV2StateMessageTextSx}>
                {stateText}
              </Text>
            </Box>
          </TableCell>
        ) : (
          <TableCell colSpan={columnCount} sx={buildTableV2StateSpacerCellSx(rowHeight)} />
        )}
      </TableRow>
    );
  });
};

export const renderTableV2FillerRows = ({
  ariaLabel,
  columnCount,
  fillerRowCount,
}: RenderTableV2FillerRowsArgs): ReactElement[] =>
  Array.from({ length: fillerRowCount }, (_item, index) => {
    const isLastFillerRow = index === fillerRowCount - 1;
    const rowHeight = resolveTableV2BodyRowHeight(!isLastFillerRow);

    return (
      <TableRow
        key={`${ariaLabel}-filler-${String(index)}`}
        data-testid="table-v2-filler-row"
        sx={buildTableV2FillerRowSx(rowHeight)}
      >
        <TableCell
          aria-hidden="true"
          colSpan={columnCount}
          sx={buildTableV2FillerCellSx({ isLastFillerRow, rowHeight })}
        >
          <Box sx={tableV2FillerBoxSx} />
        </TableCell>
      </TableRow>
    );
  });
